import React from 'react';
import * as d3 from 'd3';
import withStyles from 'isomorphic-style-loader/withStyles';
import PropTypes from 'prop-types';
import rankings from './rankings';
import s from './PresidentsGraph.css';

const maxRanking = 45;
const maxComponent = 85;
const xDim = 600;
const yDim = 450;
const xPadding = 100;
const yPadding = 25;
const innerXDim = xDim - 2 * xPadding;
const innerYDim = yDim - 2 * yPadding;
const pointRadius = 6;
const pointOpacity = 0.5;
const selectedPointColor = '#00aa55';
const pointColor = '#0055aa';
const selected = new Set([]);

function idFromName(category, name, selector = false) {
  const clean = name.toLowerCase().replace(/\./g, '').replace(/ /g, '-');
  const id = `${category}-${clean}`;
  return selector ? `#${id}` : id;
}

class PresidentsGraph extends React.Component {
  static propTypes = {
    useComponents: PropTypes.bool.isRequired,
    xIndex: PropTypes.number,
    yIndex: PropTypes.number,
  }

  componentDidMount() {
    this.drawGraph(this.props);
  }

  componentWillReceiveProps(nextProps) {
    if ('svg' in this) {
      if (this.props.useComponents === nextProps.useComponents) {
        this.updatePoints(nextProps);
      } else {
        this.drawGraph(nextProps);
      }
    }
  }

  shouldComponentUpdate(nextProps) {
    return nextProps.useComponents !== this.props.useComponents;
  }

  setTransform(boxes, props) {
    boxes
      .attr('transform',
        (d) => `translate(${this.coordTransformX(d, props)},${this.coordTransformY(d, props)})`,
      );
  }

  drawGraph(props) {
    if (this.svg) {
      d3.select('#presidents-svg').remove();
    }
    this.svg = d3.select('#presidents-graph')
      .append('svg')
      .attr('id', 'presidents-svg')
      .attr('width', xDim)
      .attr('height', yDim)
      .append('g')
      .attr('transform', `translate(${xPadding},${yPadding})`)
      .attr('width', innerXDim)
      .attr('height', innerYDim);
    this.xScale = d3.scaleLinear()
      .domain(
        props.useComponents ?
          [-maxComponent, maxComponent] :
          [0, maxRanking],
      )
      .range([0, innerXDim]);
    this.yScale = d3.scaleLinear()
      .domain(
        props.useComponents ?
          [-maxComponent, maxComponent] :
          [0, maxRanking],
      )
      .range([innerYDim, 0]);
    this.xAxis = d3.axisBottom()
      .scale(this.xScale)
      .tickValues(this.ticks(props));
    this.yAxis = d3.axisLeft()
      .scale(this.yScale)
      .tickValues(this.ticks(props));
    this.boxes = this.svg
      .selectAll('text')
      .data(rankings)
      .enter()
      .append('g')
      .attr('class', s.box)
      .attr('id', (d) => idFromName('box', d.name));
    this.points = this.boxes
      .append('circle')
      .attr('r', pointRadius)
      .attr('fill', (d) => this.pointColor(d))
      .attr('id', (d) => idFromName('point', d.name))
      .attr('opacity', pointOpacity)
      .on('mouseover', (d) => this.handleMouseover(d))
      .on('mouseout', (d) => this.handleMouseout(d))
      .on('click', (d) => this.handleClick(d));
    this.labels = this.boxes
      .append('text')
      .text((d) => d.name)
      .attr('x', 3)
      .attr('y', -10)
      .attr('id', (d) => idFromName('label', d.name))
      .attr('class', s.label)
      .style('display', (d) => this.labelDisplay(d));
    this.setTransform(this.boxes, props);

    this.svg
      .append('g')
      .attr('transform', `translate(0, ${this.yScale(0)})`)
      .call(this.xAxis);
    this.svg
      .append('g')
      .attr('transform', `translate(${this.xScale(0)}, 0)`)
      .call(this.yAxis);
  }

  updatePoints(props) {
    this.setTransform(this.boxes.transition(), props);
  }

  ticks(props) {
    if (props.useComponents) {
      return [-85, -55, -25, 25, 55, 85];
    }
    return [15, 30, 45];
  }

  coordTransformX(data, props) {
    const coords = props.useComponents ? data.components : data.rankings;
    return this.xScale(coords[props.xIndex]);
  }

  coordTransformY(data, props) {
    const coords = props.useComponents ? data.components : data.rankings;
    return this.yScale(coords[props.yIndex]);
  }

  pointColor(data) {
    return selected.has(data.name) ? selectedPointColor : pointColor;
  }

  labelDisplay(data) {
    return selected.has(data.name) ? 'initial' : 'none';
  }

  handleMouseover(d) {
    const name = d.name;
    const point = d3.select(idFromName('point', name, true));
    const label = d3.select(idFromName('label', name, true));
    point
      .attr('opacity', 1.0);
    label
      .style('display', 'initial');
  }

  handleMouseout(d) {
    const name = d.name;
    const point = d3.select(idFromName('point', name, true));
    point
      .attr('opacity', pointOpacity);
    if (!selected.has(d.name)) {
      const label = d3.select(idFromName('label', name, true));
      label
        .style('display', 'none');
    }
  }

  handleClick(data) {
    const name = data.name;
    const point = d3.select(idFromName('point', name, true));
    const label = d3.select(idFromName('label', name, true));
    if (selected.has(name)) {
      selected.delete(name);
    } else {
      selected.add(name);
    }
    point
      .attr('fill', (d) => this.pointColor(d));
    label
      .style('display', (d) => this.labelDisplay(d));
  }

  render() {
    return (
      <div id="presidents-graph" className={s.container} />
    );
  }
}

export default withStyles(s)(PresidentsGraph);



// WEBPACK FOOTER //
// src/components/PresidentsGraph/PresidentsGraph.js